import React from 'react';
import Joyride, { CallBackProps, EVENTS, Placement, TooltipRenderProps } from 'react-joyride';
import { Subtext } from '@ui-library/typography';
import styled, { useTheme } from 'styled-components';

/** Where a scrim click landed, in viewport coordinates. */
export interface SpotlightClick {
  clientX: number;
  clientY: number;
}

interface Props {
  onClose: () => void;
  /**
   * A click that lands on the spotlit element while the scrim still owns
   * pointer events. Joyride lets clicks through only once the pointer has
   * MOVED over the spotlight, so a pointer already resting there or a touch
   * tap hits the scrim instead and would otherwise read as a close. Called
   * in place of `onClose` for that click, with where it landed, so the
   * caller can treat it as the control's own click.
   */
  onSpotlightClick?: (click: SpotlightClick) => void;
  spotlight: {
    placement?: Placement;
    className: string;
    padding?: number;
    offset?: number;
    /** Corner radius of the cut-out — match the spotlit control (a pill wants 40). */
    borderRadius?: number;
  };
  text: string | JSX.Element;
  /**
   * `next`: the design-system card (Figma 2946:19120) — white 208px card,
   * 12px radius, optional title, grey body, a 16px close box holding a 6px
   * glyph, an 11.5×9 pointer, over the light navy scrim. Default: the legacy
   * primary-filled bubble.
   */
  variant?: 'next';
  /** Heading above the text — `next` only. */
  title?: string;
  // To focus on a specific element within the spotlight
  tooltip?: {
    id: string;
    placement: 'bottom' | 'top';
  };
  /** Test id on the `next` card; its close control gets `${testId}-close`. */
  testId?: string;
}

/** Figma 2946:18907 / 2946:19121. */
const NEXT_CARD_WIDTH = 208;
const NEXT_POINTER_SPREAD = 11.547;
const NEXT_POINTER_LENGTH = 9.196;
const NEXT_POINTER_INSET = 14;

/* Figma 2946:18907: 16px top/bottom, 8px sides; the rows carry the inner 8px. */
const NextCard = styled.div`
  position: relative;
  box-sizing: border-box;
  width: ${NEXT_CARD_WIDTH}px;
  padding: 16px 8px;
  border-radius: 12px;
  background: ${({ theme }) => theme.token.colorBgContainer};
  /* Invisible in light mode (the scrim gives the card its edge); in dark
     mode the card and the page share a colour, so the edge is drawn. */
  border: 1px solid ${({ theme }) => theme.token.next.coachMark.cardBorder};
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 10px;
  text-align: left;
`;

/* Both text rows are cap-trimmed in the design (text-box-trim), which is what
   makes the card 98px tall; the launcher's Beta tag uses the same trim. */
const NextTitle = styled.p`
  margin: 0;
  padding: 0 8px;
  font-size: 13px;
  font-weight: ${({ theme }) => theme.token.next.fontWeight500};
  line-height: normal;
  text-box: trim-both cap alphabetic;
  color: ${({ theme }) => theme.token.next.palette.grey800};
`;

const NextBody = styled.p`
  margin: 0;
  padding: 0 8px;
  font-size: 13px;
  line-height: 1.44;
  letter-spacing: 0.26px;
  text-box: trim-both cap alphabetic;
  /* The mock uses grey400, which is 3.3:1 on the card — under the AA floor
     for 13px text. grey500 is the nearest tone that passes in both modes. */
  color: ${({ theme }) => theme.token.next.palette.grey500};
`;

/* Figma 2946:18916 "Symbols/close-medium": a 16px box at 6px from the top and
   right edges; the glyph sits at the box's 31.25% inset (6px), grey-300, 1px. */
const NextClose = styled.button`
  position: absolute;
  top: 6px;
  right: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  color: ${({ theme }) => theme.token.next.palette.grey300};

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.token.colorPrimary};
    outline-offset: -2px;
    border-radius: 4px;
  }
`;

/** The Figma glyph, verbatim: two 1px strokes from (0.5,0.5) to (6.5,6.5). */
const NextCloseGlyph: React.FC = () => (
  <svg
    width="7"
    height="7"
    viewBox="0 0 7 7"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path d="M0.5 0.5L6.5 6.5" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M0.5 6.5L6.5 0.5" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/**
 * The `next` card as Joyride's tooltip. Joyride's own tooltip draws a 16px
 * close glyph and its own paddings; the design is pixel-specific, so the card
 * is rendered here and the library keeps the scrim, spotlight, pointer and
 * positioning.
 */
/**
 * Joyride renders its tooltip component with its own props only, so the
 * card's test id travels by context (portals keep context).
 */
const NextTooltipTestIdContext = React.createContext<string | undefined>(undefined);

const NextTooltip: React.FC<TooltipRenderProps> = ({ closeProps, step, tooltipProps }) => {
  const testId = React.useContext(NextTooltipTestIdContext);
  // Joyride labels the surface `alertdialog`; name and describe it from the
  // card's own text so assistive tech does not meet an anonymous modal.
  const id = React.useId();
  const titleId = `${id}-title`;
  const bodyId = `${id}-body`;
  return (
    <NextCard
      {...tooltipProps}
      aria-labelledby={step.title ? titleId : bodyId}
      aria-describedby={step.title ? bodyId : undefined}
      data-testid={testId}
    >
      {step.title && (
        <NextTitle id={titleId} role="heading" aria-level={2}>
          {step.title}
        </NextTitle>
      )}
      <NextBody id={bodyId}>{step.content}</NextBody>
      <NextClose type="button" {...closeProps} data-testid={testId && `${testId}-close`}>
        <NextCloseGlyph />
      </NextClose>
    </NextCard>
  );
};

export const CoachMark = ({
  onClose,
  onSpotlightClick,
  tooltip,
  spotlight,
  text,
  variant,
  title,
  testId,
}: Props) => {
  const { token } = useTheme();
  const isNext = variant === 'next';
  // A click the scrim swallowed over the spotlit element (see
  // `onSpotlightClick`). Capture phase, so it is classified before Joyride's
  // overlay turns it into a close; the flag then drops that close.
  const isSpotlightClickRef = React.useRef(false);
  React.useEffect(() => {
    if (!onSpotlightClick) {
      return undefined;
    }
    const handleClick = (event: MouseEvent) => {
      const target = event.target as Element | null;
      if (!target?.closest?.('.react-joyride__overlay')) {
        return;
      }
      const spotlit = document.querySelector(`.${spotlight.className}`);
      if (!spotlit) {
        return;
      }
      const { left, right, top, bottom } = spotlit.getBoundingClientRect();
      const { clientX, clientY } = event;
      if (clientX >= left && clientX <= right && clientY >= top && clientY <= bottom) {
        isSpotlightClickRef.current = true;
        onSpotlightClick({ clientX, clientY });
      }
    };
    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [onSpotlightClick, spotlight.className]);

  const legacyStyles = {
    buttonClose: {
      top: 6,
      right: 8,
      padding: 0,
      // The bubble is filled with `colorPrimary`, which the dark palette
      // lightens; `colorBgContainer` inverts with the theme and so stays
      // legible on that fill in either mode.
      color: token.colorBgContainer,
      width: 10,
      height: 10,
    },
    tooltipContent: {
      padding: 0,
    },
    tooltip: {
      borderRadius: token.borderRadiusLG,
      padding: '12px 26px 12px 12px',
    },
    tooltipContainer: {
      textAlign: 'left' as const,
    },
    options: {
      width: 230,
      backgroundColor: token.colorPrimary,
      arrowColor: token.colorPrimary,
      borderRadius: token.borderRadiusLG,
    },
  };

  const nextStyles = {
    spotlight: {
      borderRadius: spotlight.borderRadius ?? 8,
    },
    options: {
      width: NEXT_CARD_WIDTH,
      backgroundColor: token.colorBgContainer,
      arrowColor: token.colorBgContainer,
      // Figma 2944:18848: a light navy wash, not black; tokenised per mode.
      overlayColor: token.next.coachMark.scrim,
    },
  };

  const handleCallback = (data: CallBackProps) => {
    // The X and a click on the scrim both arrive as `close`. Joyride reports
    // it twice (after the step, then at tour end); the next card acts on one,
    // the legacy bubble keeps its callers' long-standing double call.
    if (data.action !== 'close') {
      return;
    }
    if (isNext) {
      if (data.type !== EVENTS.STEP_AFTER) {
        return;
      }
      if (isSpotlightClickRef.current) {
        isSpotlightClickRef.current = false;
        return;
      }
    }
    onClose();
  };

  const options = {
    steps: [
      {
        disableBeacon: true,
        hideFooter: true,
        spotlightClicks: true,
        placement: spotlight.placement,
        target: `.${spotlight.className}`,
        offset: spotlight.offset || 10,
        ...(isNext ? { title } : {}),
        content: isNext ? text : <Subtext color={token.colorBgContainer}>{text}</Subtext>,
      },
    ],
    ...(!!tooltip && {
      floaterProps: {
        target: `#${tooltip.id}`,
        placement: tooltip.placement,
      },
    }),
    ...(isNext && {
      floaterProps: {
        ...(tooltip ? { target: `#${tooltip.id}`, placement: tooltip.placement } : {}),
        // Popper would centre the pointer on the target; the design pins it
        // 14px in from the card's leading edge (the `margin` below), so the
        // arrow modifier is switched off and the margin alone places it.
        options: { arrow: { enabled: false } },
        styles: {
          // Figma 2946:19121 "Polygon 3": 11.5 wide, 9 tall, 14px in from the edge.
          arrow: {
            length: NEXT_POINTER_LENGTH,
            spread: NEXT_POINTER_SPREAD,
            margin: NEXT_POINTER_INSET,
          },
          // The design's card is flat; drop the floater's default drop-shadow.
          floater: { filter: 'none' },
        },
      },
      tooltipComponent: NextTooltip,
    }),
  };

  return (
    <NextTooltipTestIdContext.Provider value={testId}>
      <Joyride
        callback={handleCallback}
        {...options}
        styles={isNext ? nextStyles : legacyStyles}
        disableScrolling
        spotlightPadding={spotlight.padding || 0}
      />
    </NextTooltipTestIdContext.Provider>
  );
};
